// Copyright (c) StrangeBee 2023

import { useContext } from 'react';
import { DashboardRowOutput, DefinitionOutput, WidgetOutput } from 'thehive-sdk';
import { generateId } from '@Helpers/';
import { DashboardContextProps, DashboardContext } from '@Context/DashboardContext';

type DashboardContextReturn = DashboardContextProps & {
    createWidget: (widget: WidgetOutput, rowIndex?: number, widgetIndex?: number) => void;
    setRow: (rowData: DashboardRowOutput, rowIndex: number) => void;
    setRows: (rows: DashboardRowOutput[]) => void;
    editWidget: (widget: WidgetOutput, widgetId: string) => void;
};

export const useDashboard = (): DashboardContextReturn => {
    const { dashboard, setDashboard, editMode, setEditMode, widgets } = useContext(DashboardContext) || {};

    const setDefinition = (definition: DefinitionOutput) => {
        setDashboard?.((dashboard) => ({
            ...dashboard,
            definition,
        }));
    };

    const setRows = (rows: DashboardRowOutput[]) => {
        const newDefinition = {
            ...(dashboard?.definition || { period: 'all', customPeriod: {} }),
            items: rows,
        };
        setDefinition(newDefinition);
    };

    const setRow = (rowData: DashboardRowOutput, rowIndex: number) => {
        const newRows = dashboard?.definition.items.map((row, index) => (index === rowIndex ? rowData : row)) || [];
        setRows(newRows);
    };

    const editWidget = (widget: WidgetOutput, widgetId: string): void => {
        // replacing the existing widget by its id

        setDashboard?.((dashboard) => ({
            ...dashboard,
            definition: {
                ...dashboard.definition,
                items: dashboard.definition.items.map((container) => ({
                    ...container,
                    items: container.items.map((existingWidget) =>
                        existingWidget.id === widgetId ? widget : existingWidget,
                    ),
                })),
            },
        }));
    };

    const createWidget = (widget: WidgetOutput, rowIndex: number, widgetIndex?: number): void => {
        setDashboard?.((dashboard) => {
            const dashboardToBeEdited = { ...dashboard };

            // creating a random id for the widget
            widget.id = generateId();

            // adding a row if the items array is empty
            if (dashboardToBeEdited.definition.items.length === 0) {
                dashboardToBeEdited.definition.items.push({ type: 'container', items: [] });
            }

            // checking widget id to create a unique one for ours
            dashboardToBeEdited.definition.items.forEach((container) => {
                container.items.forEach((existingWidget) => {
                    if (existingWidget.id === widget.id) {
                        widget.id = generateId();
                    }
                });
            });

            // adding the widget to an available row
            // increasing the id if it already exists
            dashboardToBeEdited.definition.items.some((container, index) => {
                if (index === rowIndex) {
                    if (!widgetIndex && widgetIndex !== 0) {
                        container.items.push(widget);
                    } else {
                        container.items.splice(widgetIndex, 0, widget);
                    }
                    return true;
                }

                // if every row is full, adding a row with the widget in it
                if (index === dashboardToBeEdited.definition.items.length - 1) {
                    dashboardToBeEdited.definition.items.push({ type: 'container', items: [widget] });
                }
                return false;
            });

            return {
                ...dashboardToBeEdited,
            };
        });
    };

    return {
        dashboard,
        setDashboard,
        editMode,
        setEditMode,
        setRow,
        setRows,
        createWidget,
        editWidget,
        widgets,
    } as DashboardContextReturn;
};
